import { useMemo } from "react";

import { Box, Paper, Tooltip } from "@material-ui/core";

import { CoinInfo, NumericText, PaperTitle, Table, TableSkeleton } from "components";

import get from "lodash/get";
import { useBorrowContext } from "context/contracts/BorrowContext";
import { useGetTokensWithoutAccount } from "pages/BorrowerNewDashboard/hooks/useTokenSupported";
import { useWallet } from "hooks";
import { useFetchGraphData } from "hooks/query/graphQL/useFetchGraphData";
import { whiteBgForLogo } from "utils/utils";
import { useAccountBalance } from "../../hooks";

const COLUMNS = [
  {
    Header: () => (
      <Tooltip title="List of assets that can be borrowed on the platform. " arrow placement="top">
        <Box display="inline">Asset</Box>
      </Tooltip>
    ),
    accessor: "symbol",
    props: {
      width: 200,
    },
    Cell: ({ row: { original }, value }) => (
      <CoinInfo whiteBg={whiteBgForLogo(value)} logoUrl={get(original, "logo")}>
        {value}
      </CoinInfo>
    ),
  },
  {
    Header: () => (
      <Tooltip title="Token count held in your external wallet." arrow placement="top">
        <Box display="inline">Wallet Balance</Box>
      </Tooltip>
    ),
    accessor: "balanceOf",
    Cell: ({ value }) => <NumericText value={value} precision={2} />,
  },
  {
    Header: () => (
      <Tooltip
        title="TOTAL lender deposits for this lending pool minus TOTAL outstanding."
        arrow
        placement="top"
      >
        <Box display="inline">Available to borrow</Box>
      </Tooltip>
    ),
    accessor: "cash",
    Cell: ({ value }) => <NumericText value={value} precision={2} />,
  },
  {
    Header: () => (
      <Tooltip
        title="TOTAL lender deposits for this lending pool minus TOTAL outstanding."
        arrow
        placement="top"
      >
        <Box display="inline">Borrow APY</Box>
      </Tooltip>
    ),
    accessor: "borrowAPY",
    Cell: ({ value }) => <NumericText value={value?.amount || 0} suffix="%" precision={2} />,
  },
];

const AvailableToReturnTable = () => {
  const { account } = useWallet();
  const columns = useMemo(() => COLUMNS, []);
  const context = useBorrowContext();
  const cash = useMemo(() => get(context, ["cashFToken"], []), [context]);
  const userTokenInfo = useMemo(() => get(context, ["userTokenInfo"], []), [context]);

  const { getBorrowingApy } = useFetchGraphData();

  const {
    data: dummyData,
    isLoading: dummyLoading,
    error: dummyError,
  } = useGetTokensWithoutAccount();

  const { isLoading } = useAccountBalance();

  const fakeData = [
    {
      balanceOf: "0.000",
      symbol: "USDC",
      logo: "/assets/coins_list/usd-coin.svg",
    },
    {
      balanceOf: "0.000",
      symbol: "USB",
      logo: "/assets/images/usb_token.png",
    },
  ];
  return (
    <Paper>
      <PaperTitle>Lending Assets</PaperTitle>
      <Box mt={2}>
        {!account &&
          ((!dummyData || dummyLoading) && !dummyError ? (
            <TableSkeleton rows={6} />
          ) : (
            <Table columns={columns} data={!dummyData ? fakeData : dummyData.lendingTokens} />
          ))}
        {isLoading || !userTokenInfo.length ? (
          account && <TableSkeleton rows={6} />
        ) : (
          <Table
            columns={columns}
            data={userTokenInfo
              .filter((o) => o?.type === "lendingToken")
              .map((o) => {
                const borrowAPY = getBorrowingApy(o.address);

                return {
                  ...o,
                  cash: get(cash, [o.address, "cash"], 0),
                  borrowAPY,
                };
              })}
          />
        )}
      </Box>
    </Paper>
  );
};

export default AvailableToReturnTable;
